import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutlet } from '../lib/OutletContext';
import { useAuth } from '../lib/AuthContext';
import {
  getRecipes, publishRecipe, archiveRecipe, rollupRecipeCost, deleteRecipe,
  getSalesCaptureSyncRuns, getSalesCaptureUnmappedImpact, getIngredientsMaster,
  getPreps,
} from '../lib/api';
import { BookOpen, Plus, Search, RefreshCw, AlertCircle, Check, Archive, Calculator, Link2, Trash2, ShieldCheck, Pencil, Upload, Download } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { exportRecipesCsv, exportRecipesXlsx } from '../lib/exportTemplate';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import logger from '../lib/logger';
import RecipeEditorDialog from '../components/RecipeEditorDialog';
import RecipeUploadDialog from '../components/RecipeUploadDialog';

const CONFIDENCE_STYLES = {
  LOW:    { label: 'LOW',    cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  MEDIUM: { label: 'MEDIUM', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  HIGH:   { label: 'HIGH',   cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
};

const COVERAGE_THRESHOLD = 85;

const RecipeLibrary = () => {
  const navigate = useNavigate();
  const { selectedOutlet } = useOutlet();
  const { user } = useAuth();
  const canEdit = !!(user?.is_org_admin || user?.is_super_admin);
  const [recipes, setRecipes] = useState([]);
  const [recipesCount, setRecipesCount] = useState(0);
  const [ingredients, setIngredients] = useState([]);
  const [preps, setPreps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced search — 250ms after the user stops typing. Avoids
  // a request-storm at scale and decouples keystrokes from the API hit.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  // Operator-explicit selection set (for "Selected only" export). Never auto-
  // populated from filters — must be explicit per user spec.
  const [selectedRecipeIds, setSelectedRecipeIds] = useState(new Set());

  const toggleRecipeSelected = useCallback((recipeId) => {
    setSelectedRecipeIds((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  }, []);
  const clearRecipeSelection = useCallback(() => setSelectedRecipeIds(new Set()), []);

  // Editor / Upload dialogs
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [editorRecipe, setEditorRecipe] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Availability dashboard — sourced from Sales Capture latest run + unmapped-impact
  const [dashboard, setDashboard] = useState({
    publishedMenu: 0,
    draftMenu: 0,
    topUnmapped: 0,
    coveragePct: null,
  });

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedOutlet) params.outlet_id = selectedOutlet;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      // Recipe-list call is the only request this effect should drive.
      // Side-fetches (ingredients, preps, dashboard) live in their own
      // effects so they do NOT run on every keystroke / search change.
      const data = await getRecipes(params);
      setRecipes(data.recipes || []);
      setRecipesCount(typeof data.count === 'number' ? data.count : (data.recipes || []).length);
    } catch (error) {
      toast.error('Failed to load recipes');
      logger.error('[RecipeLibrary] Error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet, statusFilter, typeFilter, debouncedSearch]);

  // Side data: ingredients + preps. Refetch only when outlet changes.
  // These are needed for the export lookup map and the editor pickers,
  // not for filtering — so they should NEVER be tied to the search term.
  const loadSideData = useCallback(async () => {
    try {
      const [ingResp, prepsResp] = await Promise.all([
        selectedOutlet ? getIngredientsMaster(selectedOutlet, true).catch(() => null) : Promise.resolve(null),
        getPreps().catch(() => ({ preps: [] })),
      ]);
      const ingList = Array.isArray(ingResp) ? ingResp : (ingResp?.ingredients || []);
      setIngredients(ingList);
      setPreps(Array.isArray(prepsResp) ? prepsResp : (prepsResp?.preps || []));
    } catch (e) {
      logger.warn('[RecipeLibrary] side data fetch failed', e);
    }
  }, [selectedOutlet]);

  // Dashboard tiles — same outlet-only dependency.
  const loadDashboard = useCallback(async () => {
    try {
      const allMenu = await getRecipes({
        ...(selectedOutlet ? { outlet_id: selectedOutlet } : {}),
        type: 'MENU_ITEM',
      });
      const list = allMenu.recipes || [];
      const published = list.filter((r) => r.status === 'PUBLISHED').length;
      const draft = list.filter((r) => r.status === 'DRAFT').length;

      let coveragePct = null;
      let topUnmapped = 0;
      if (selectedOutlet) {
        try {
          const runs = await getSalesCaptureSyncRuns(selectedOutlet, 1);
          coveragePct = runs?.runs?.[0]?.mapping_coverage ?? null;
        } catch (e) { logger.warn('[RecipeLibrary] sync-runs fetch failed', e); }
        try {
          const impact = await getSalesCaptureUnmappedImpact(selectedOutlet, 'quantity', 200);
          topUnmapped = impact?.unique_unmapped_items ?? (impact?.items?.length || 0);
        } catch (e) { logger.warn('[RecipeLibrary] unmapped-impact fetch failed', e); }
      }
      setDashboard({ publishedMenu: published, draftMenu: draft, topUnmapped, coveragePct });
    } catch (e) {
      logger.warn('[RecipeLibrary] dashboard fetch failed', e);
    }
  }, [selectedOutlet]);

  useEffect(() => { loadRecipes(); }, [loadRecipes]);
  useEffect(() => { loadSideData(); }, [loadSideData]);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // ── Export helpers ──────────────────────────────────────────────
  const _buildLookups = () => {
    const ingMap = new Map((ingredients || []).map((i) => [i.ingredient_id, i]));
    const prepByPrepId = new Map((preps || []).map((p) => [p.prep_id, p]));
    const prepByActsAsId = new Map(
      (preps || []).filter((p) => p.acts_as_ingredient_id)
                   .map((p) => [p.acts_as_ingredient_id, p])
    );
    return { ingMap, prepByPrepId, prepByActsAsId };
  };

  const doExportRecipes = (list, fmt, scopeLabel) => {
    if (!list?.length) {
      toast.error('Nothing to export.');
      return;
    }
    const { ingMap, prepByPrepId, prepByActsAsId } = _buildLookups();
    const fn = fmt === 'csv' ? exportRecipesCsv : exportRecipesXlsx;
    const n = fn(list, ingMap, prepByPrepId, prepByActsAsId);
    toast.success(`Exported ${list.length} recipe${list.length === 1 ? '' : 's'} (${n} line${n === 1 ? '' : 's'}, ${scopeLabel}, ${fmt.toUpperCase()}).`);
  };

  const doExportRecipesAll = async (fmt) => {
    try {
      const params = {};
      if (selectedOutlet) params.outlet_id = selectedOutlet;
      const data = await getRecipes(params);
      const all = data?.recipes || [];
      doExportRecipes(all, fmt, 'all');
    } catch (e) {
      logger.error('[RecipeLibrary] export all failed:', e);
      toast.error('Failed to fetch all recipes for export.');
    }
  };

  const handlePublish = async (recipe) => {
    setActionLoading(recipe.recipe_id);
    try {
      await publishRecipe(recipe.recipe_id);
      toast.success(`${recipe.recipe_name} published`);
      loadRecipes();
    } catch (error) {
      toast.error('Failed to publish recipe');
      logger.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (recipe) => {
    setActionLoading(recipe.recipe_id);
    try {
      await archiveRecipe(recipe.recipe_id);
      toast.success(`${recipe.recipe_name} archived`);
      loadRecipes();
    } catch (error) {
      toast.error('Failed to archive recipe');
      logger.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRollupCost = async (recipe) => {
    setActionLoading(recipe.recipe_id);
    try {
      const result = await rollupRecipeCost(recipe.recipe_id);
      toast.success(`Cost updated: ${formatCurrency(result.unit_cost)}/unit`);
      loadRecipes();
    } catch (error) {
      toast.error('Failed to rollup cost');
      logger.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (recipe) => {
    if (!window.confirm(`Are you sure you want to delete "${recipe.recipe_name}"? This action cannot be undone.`)) {
      return;
    }
    setActionLoading(recipe.recipe_id);
    try {
      await deleteRecipe(recipe.recipe_id);
      toast.success(`${recipe.recipe_name} deleted`);
      loadRecipes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete recipe';
      toast.error(message);
      logger.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
      PUBLISHED: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      ARCHIVED: 'bg-red-500/10 text-red-400 border-red-500/20',
    };
    return styles[status] || styles.DRAFT;
  };

  const getTypeBadge = (type) => {
    return type === 'MENU_ITEM' 
      ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
      : 'bg-purple-500/10 text-purple-400 border-purple-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recipe-library-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="recipe-library-title">Recipes</h1>
          <p className="text-zinc-400">Recipe library with costing and POS mapping</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/recipes/mapping')}
            className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm font-medium"
            data-testid="menu-mapping-button"
          >
            <Link2 className="w-4 h-4" />
            Menu Mapping
          </button>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={!recipes?.length}
                  className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors text-sm font-medium"
                  data-testid="export-recipes-button"
                  title="Export recipes in the bulk-import format (round-trips)"
                >
                  <Download className="w-4 h-4" />
                  Export Recipes
                  {selectedRecipeIds.size > 0 && (
                    <span
                      className="ml-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[10px] font-bold"
                      data-testid="export-selected-counter"
                    >
                      {selectedRecipeIds.size} selected
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#18181b] border-[#27272a]">
                {selectedRecipeIds.size > 0 && (
                  <>
                    <DropdownMenuItem
                      onClick={() => doExportRecipes(
                        recipes.filter((r) => selectedRecipeIds.has(r.recipe_id)),
                        'csv', `selected (${selectedRecipeIds.size})`
                      )}
                      data-testid="export-recipes-csv-selected"
                    >Selected ({selectedRecipeIds.size}) – CSV</DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => doExportRecipes(
                        recipes.filter((r) => selectedRecipeIds.has(r.recipe_id)),
                        'xlsx', `selected (${selectedRecipeIds.size})`
                      )}
                      data-testid="export-recipes-xlsx-selected"
                    >Selected ({selectedRecipeIds.size}) – Excel</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => doExportRecipes(recipes, 'csv', 'visible')}
                  data-testid="export-recipes-csv-visible"
                >Visible (CSV)</DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => doExportRecipes(recipes, 'xlsx', 'visible')}
                  data-testid="export-recipes-xlsx-visible"
                >Visible (Excel)</DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => doExportRecipesAll('csv')}
                  data-testid="export-recipes-csv-all"
                >All (CSV)</DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => doExportRecipesAll('xlsx')}
                  data-testid="export-recipes-xlsx-all"
                >All (Excel)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canEdit && (
            <button
              onClick={() => setUploadOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm font-medium"
              data-testid="upload-recipes-button"
            >
              <Upload className="w-4 h-4" />
              Upload Recipes
            </button>
          )}
          <button
            onClick={() => { setEditorMode('create'); setEditorRecipe(null); setEditorOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md transition-colors text-sm font-bold text-white"
            data-testid="create-recipe-button"
          >
            <Plus className="w-4 h-4" />
            New Recipe
          </button>
        </div>
      </div>

      {/* Availability Dashboard — ties recipes to the Phase 2 coverage gate */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="recipe-dashboard">
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-published-menu">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Published Menu</p>
          <p className="text-2xl font-mono font-bold text-emerald-500">{dashboard.publishedMenu}</p>
          <p className="text-xs text-zinc-500 mt-1">usable for sales consumption</p>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-draft-menu">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Draft Menu</p>
          <p className="text-2xl font-mono font-bold text-zinc-300">{dashboard.draftMenu}</p>
          <p className="text-xs text-zinc-500 mt-1">in progress, not live</p>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-top-unmapped">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Unmapped Sales Items</p>
          <p className="text-2xl font-mono font-bold text-amber-500">{dashboard.topUnmapped}</p>
          <p className="text-xs text-zinc-500 mt-1">need recipe + mapping</p>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-coverage">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Mapping Coverage</p>
          <p className={cn(
            'text-2xl font-mono font-bold',
            dashboard.coveragePct === null ? 'text-zinc-500' :
              dashboard.coveragePct >= COVERAGE_THRESHOLD ? 'text-emerald-500' : 'text-amber-500'
          )}>
            {dashboard.coveragePct === null ? '—' : `${dashboard.coveragePct.toFixed(1)}%`}
          </p>
          <p className="text-xs text-zinc-500 mt-1">Phase 2 gate: {COVERAGE_THRESHOLD}%</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by name, ingredient, prep, POS name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-20 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              data-testid="search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-[#27272a] rounded"
                data-testid="search-clear-button"
                title="Clear search"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none text-sm"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="PUBLISHED">Published</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none text-sm"
          data-testid="type-filter"
        >
          <option value="">All Types</option>
          <option value="MENU_ITEM">Menu Item</option>
          <option value="PREP">Prep</option>
        </select>
        <button
          onClick={loadRecipes}
          className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm"
          data-testid="refresh-button"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Result count — context for the search/filter combination. */}
      <div className="flex items-center justify-between mb-3 text-xs text-zinc-500" data-testid="recipes-result-count">
        <span>
          {debouncedSearch
            ? <>Showing <span className="text-zinc-200 font-medium">{recipesCount}</span> of all recipes for &ldquo;{debouncedSearch}&rdquo;</>
            : <>{recipesCount} recipe{recipesCount === 1 ? '' : 's'}</>}
        </span>
        {searchTerm && searchTerm !== debouncedSearch && (
          <span className="text-zinc-500 italic">searching…</span>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden" data-testid="recipes-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#09090b] border-b border-[#27272a]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Recipe Name</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Confidence</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Unit Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Yield</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Updated</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272a]">
              {recipes.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? "9" : "8"} className="px-4 py-12 text-center text-zinc-500" data-testid="recipes-empty-state">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    {debouncedSearch ? (
                      <>
                        <p>No recipes match &ldquo;{debouncedSearch}&rdquo;.</p>
                        <button
                          onClick={() => setSearchTerm('')}
                          className="mt-3 text-emerald-500 hover:text-emerald-400 text-sm font-medium"
                          data-testid="recipes-empty-clear-search"
                        >
                          Clear search
                        </button>
                      </>
                    ) : (
                      <>
                        <p>No recipes found</p>
                        <button
                          onClick={() => navigate('/recipes/new')}
                          className="mt-3 text-emerald-500 hover:text-emerald-400 text-sm font-medium"
                        >
                          Create your first recipe
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                recipes.map((recipe) => (
                  <tr
                    key={recipe.recipe_id}
                    className="hover:bg-[#09090b] transition-colors cursor-pointer"
                    onClick={() => navigate(`/recipes/${recipe.recipe_id}`)}
                    data-testid="recipe-row"
                  >
                    {canEdit && (
                      <td
                        className="px-3 py-4 w-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="cursor-pointer accent-emerald-500"
                          checked={selectedRecipeIds.has(recipe.recipe_id)}
                          onChange={() => toggleRecipeSelected(recipe.recipe_id)}
                          data-testid={`recipe-select-${recipe.recipe_id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-medium" data-testid="recipe-name">{recipe.recipe_name}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                            {recipe.category && (
                              <span className="text-[10px] text-zinc-400 bg-[#09090b] border border-[#27272a] rounded px-1.5 py-0.5" data-testid="recipe-category">
                                {recipe.category}
                              </span>
                            )}
                            {recipe.category_source === 'sales_capture_mapping' && (
                              <span
                                className="text-[10px] text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded px-1.5 py-0.5 flex items-center gap-1"
                                title={`Inherited from "${recipe.category_populated_from_item_name || 'POS item'}" via Sales Capture mapping`}
                                data-testid="recipe-category-source-pos"
                              >
                                POS category
                              </span>
                            )}
                            {recipe.costing?.missing_cost_lines > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-amber-500">
                                <AlertCircle className="w-3 h-3" />
                                {recipe.costing.missing_cost_lines} missing costs
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={cn('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border', getTypeBadge(recipe.recipe_type))}>
                        {recipe.recipe_type === 'MENU_ITEM' ? 'Menu' : 'Prep'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={cn('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border', getStatusBadge(recipe.status))}>
                        {recipe.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center" data-testid="recipe-confidence-cell">
                      {(() => {
                        const c = recipe.recipe_confidence || 'LOW';
                        const s = CONFIDENCE_STYLES[c] || CONFIDENCE_STYLES.LOW;
                        return (
                          <span className={cn('inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border', s.cls)}>
                            <ShieldCheck className="w-3 h-3" />
                            {s.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono text-sm">
                        {formatCurrency(recipe.costing?.unit_cost || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono text-sm text-zinc-400">
                        {recipe.yields?.yield_qty || 1} {recipe.yields?.yield_unit || 'portion'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-xs text-zinc-400">
                        {formatDateTime(recipe.updated_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        {canEdit && (
                          <button
                            onClick={() => { setEditorMode('edit'); setEditorRecipe(recipe); setEditorOpen(true); }}
                            disabled={actionLoading === recipe.recipe_id}
                            className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-400 transition-colors"
                            title="Edit"
                            data-testid="edit-recipe-button"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {recipe.status === 'DRAFT' && (
                          <button
                            onClick={() => handlePublish(recipe)}
                            disabled={actionLoading === recipe.recipe_id}
                            className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-500 transition-colors"
                            title="Publish"
                            data-testid="publish-recipe-button"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {recipe.status === 'PUBLISHED' && (
                          <button
                            onClick={() => handleArchive(recipe)}
                            disabled={actionLoading === recipe.recipe_id}
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400 transition-colors"
                            title="Archive"
                            data-testid="archive-recipe-button"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRollupCost(recipe)}
                          disabled={actionLoading === recipe.recipe_id}
                          className="p-1.5 rounded-md hover:bg-blue-500/10 text-blue-400 transition-colors"
                          title="Rollup Cost"
                          data-testid="rollup-cost-button"
                        >
                          <Calculator className="w-4 h-4" />
                        </button>
                        {(recipe.status === 'DRAFT' || recipe.status === 'ARCHIVED') && (
                          <button
                            onClick={() => handleDelete(recipe)}
                            disabled={actionLoading === recipe.recipe_id}
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400 transition-colors"
                            title="Delete"
                            data-testid="delete-recipe-button"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      {recipes.length > 0 && (
        <div className="flex items-center justify-between text-sm text-zinc-400">
          <span>{recipes.length} recipe{recipes.length !== 1 ? 's' : ''}</span>
          <span>
            {recipes.filter(r => r.recipe_type === 'MENU_ITEM' && r.status === 'DRAFT').length} menu draft{recipes.filter(r => r.recipe_type === 'MENU_ITEM' && r.status === 'DRAFT').length !== 1 ? 's' : ''} awaiting publish
          </span>
        </div>
      )}

      <RecipeEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        mode={editorMode}
        existingRecipe={editorRecipe}
        ingredients={ingredients}
        outletId={selectedOutlet}
        onSaved={loadRecipes}
      />
      <RecipeUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        ingredients={ingredients}
        preps={preps}
        existingRecipes={recipes}
        existingRecipeNames={recipes.map((r) => r.recipe_name)}
        outletId={selectedOutlet}
        onImported={loadRecipes}
      />
    </div>
  );
};

export default RecipeLibrary;
